import { Component, Input, OnChanges, input, inject } from '@angular/core';
import { Employee } from '../../../classes/models/employee/Employee';
import { StoreEmployeeAvailability } from '../../../classes/models/employee/StoreEmployeeAvailability';
import { dayOfWeekValues } from '../../../classes/enums/DayOfWeek';
import moment from 'moment';
import { GroupedAvailability } from '../../../classes/models/employee/GroupedAvailability';
import { GroupAvailabilitiesService } from '../../../services/employee/group-availabilities.service';
import { KeyValue } from '@angular/common';
import { EmployeeRoleEnum } from '../../../classes/enums/EmployeeRoleEnum';
import { SHARED_IMPORTS } from '../../../shared/standalone-imports';

/**
 * This component displays a calendar of availabilities for all employees of the selected store.
 */
@Component({
    selector: 'app-employees-availabilities',
    templateUrl: './employees-availabilities.component.html',
    styleUrls: ['./employees-availabilities.component.less'],
    imports: [SHARED_IMPORTS]
})
export class EmployeesAvailabilitiesComponent implements OnChanges {
  private groupAvailabilitiesService = inject(GroupAvailabilitiesService);


  /** An identifier of a store. */
  readonly storeId = input<string>(undefined);
  /** A list of all employees of the store. */
  @Input() employees: Employee[];
  /** A Monday of the week, for which availability are displayed. */
  @Input() selectedMonday: string;
  /** A list of each date in a week. */
  readonly dates = input<string[]>(undefined);
  /** Availabilities for each employee. */
  readonly availabilities = input<StoreEmployeeAvailability[]>(undefined);

  /** A map from employee to availabilities list, grouped by time range. */
  map: Map<Employee, GroupedAvailability[]>;

  /** Width (in pixels) for the first column of the calendar, where an employee name is displayed. */
  firstColumnWidth = 200;
  /** Width (in pixels) for other columns of the calendar, where availabilities for each weekday are displayed. */
  otherColumnsWidth = 130;

  /**
   * Updates a map of grouped availabilities on any change of input data.
   */
  ngOnChanges(): void {
    this.map = this.groupAvailabilitiesService.groupAvailabilities(this.selectedMonday, this.availabilities(), this.employees);
  }

  /**
   * Finds how many pixels from the left side of the calendar should be the start of the time range box to display.
   * @param groupedAvailability a grouped availability to display.
   */
  getAvailabilityLeftPixels(groupedAvailability: GroupedAvailability): number {
    for (const dayOfWeek of dayOfWeekValues()) {
      if (groupedAvailability.dayOfWeeks.includes(dayOfWeek)) {
        const index = moment(dayOfWeek.toLowerCase(), 'dddd').isoWeekday();
        return this.firstColumnWidth + this.otherColumnsWidth * (index - 1) + 5;
      }
    }
  }

  /**
   * Finds the width (in pixels) of the availability time range box to display.
   * @param groupedAvailability a grouped availability to display.
   */
  getAvailabilityWidthPixels(groupedAvailability: GroupedAvailability) {
    return (this.otherColumnsWidth * groupedAvailability.dayOfWeeks.length) - 10;
  }

  /**
   * Finds the width (in pixels) of horizontal line, which is displayed inside the availability time range box.
   * @param groupedAvailability a grouped availability to display.
   */
  getHorizontalLineWidthPixels(groupedAvailability: GroupedAvailability) {
    return (this.otherColumnsWidth * groupedAvailability.dayOfWeeks.length) - 120;
  }

  /**
   * Sorts key and value pairs of employee's grouped availabilities by the original order of {@link EmployeeRoleEnum}.
   * @param a the first element for comparison.
   * @param b the second element for comparison.
   */
  sortByEmployeeRole = (a: KeyValue<Employee, GroupedAvailability[]>, b: KeyValue<Employee, GroupedAvailability[]>): number => {
    return Object.values(EmployeeRoleEnum).indexOf(a.key.role) > Object.values(EmployeeRoleEnum).indexOf(b.key.role) ? 1 : -1;
  }
}
